// Wave S — Multi-Language AI: i18n cache, useT hook, language preference.
import { useCallback, useEffect, useState } from "react";
import api from "./api";

// ────────────────────────────────────────────────────────────────────
// In-memory + localStorage cache keyed by language code.
// Updated whenever the language picker saves; all subscribers re-render.
// ────────────────────────────────────────────────────────────────────
const LANG_KEY = "rachana_language";
const STRINGS_KEY = "rachana_ui_strings";
let _langCache = null;
let _stringsCache = {};
const _langListeners = new Set();
const _stringsListeners = new Set();

function _readLang() {
  if (_langCache) return _langCache;
  try {
    _langCache = localStorage.getItem(LANG_KEY) || "en";
  } catch (_) { _langCache = "en"; }
  return _langCache;
}

function _readStringsForLang(lang) {
  if (_stringsCache[lang]) return _stringsCache[lang];
  try {
    const raw = localStorage.getItem(`${STRINGS_KEY}__${lang}`);
    if (raw) _stringsCache[lang] = JSON.parse(raw);
  } catch (_) { /* ok */ }
  return _stringsCache[lang] || null;
}

export function getCurrentLanguage() { return _readLang(); }

export function setCurrentLanguage(lang) {
  _langCache = lang || "en";
  try { localStorage.setItem(LANG_KEY, _langCache); } catch (_) { /* ok */ }
  _langListeners.forEach(fn => { try { fn(_langCache); } catch (_) {} });
  // Apply RTL on <html> for ar/ur immediately.
  try {
    const isRtl = ["ar", "ur"].includes(_langCache);
    document.documentElement.setAttribute("dir", isRtl ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", _langCache);
  } catch (_) { /* ok */ }
}

export function setStringsForLang(lang, strings, rtl = false) {
  _stringsCache[lang] = strings || {};
  try {
    localStorage.setItem(`${STRINGS_KEY}__${lang}`,
        JSON.stringify(_stringsCache[lang]));
  } catch (_) { /* ok */ }
  _stringsListeners.forEach(fn => { try { fn(); } catch (_) {} });
  // Apply RTL whenever strings include rtl flag.
  if (rtl) {
    try { document.documentElement.setAttribute("dir", "rtl"); }
    catch (_) { /* ok */ }
  }
}

// Lazy-fetch UI strings for a language; cache for the session.
export async function loadUiStrings(lang) {
  if (_stringsCache[lang]) return _stringsCache[lang];
  try {
    const r = await api.get(`/language/ui/${lang}`);
    if (r.data?.strings) {
      setStringsForLang(lang, r.data.strings, r.data.rtl);
      return r.data.strings;
    }
  } catch (_) { /* ignore — fall back to English defaults */ }
  return null;
}

// Refresh user's saved language preference from the server.
export async function refreshLanguageFromServer() {
  try {
    const me = await api.get("/auth/me");
    const userId = me.data?.id || me.data?.user?.id;
    if (!userId) return;
    const r = await api.get(`/users/${userId}/language`);
    if (r.data?.language) {
      setCurrentLanguage(r.data.language);
      await loadUiStrings(r.data.language);
    }
  } catch (_) { /* not logged in or no pref yet */ }
}

// ────────────────────────────────────────────────────────────────────
// Hooks
// ────────────────────────────────────────────────────────────────────
export function useLanguage() {
  const [lang, setLang] = useState(_readLang());
  useEffect(() => {
    const fn = (l) => setLang(l);
    _langListeners.add(fn);
    return () => { _langListeners.delete(fn); };
  }, []);
  return lang;
}

/** useT(): returns a `t(key, fallback?)` function that translates UI strings.
 *  Falls back to ``fallback`` (or the key humanised) when the string is
 *  missing for the active language.
 */
export function useT() {
  const lang = useLanguage();
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force(n => n + 1);
    _stringsListeners.add(fn);
    return () => { _stringsListeners.delete(fn); };
  }, []);
  // Trigger an async fetch of the strings if we don't have them yet.
  useEffect(() => {
    if (!_stringsCache[lang]) {
      loadUiStrings(lang).catch(() => {});
    }
  }, [lang]);

  return useCallback((key, fallback) => {
    const dict = _readStringsForLang(lang) || _stringsCache.en || {};
    if (dict[key]) return dict[key];
    if (_stringsCache.en?.[key]) return _stringsCache.en[key];
    return fallback ?? key.replace(/_/g, " ").replace(/\b\w/g,
        c => c.toUpperCase());
  }, [lang]);
}
